import { usePermisos } from "../../context/PermisosContext";
// src/components/Sensores/Sensores.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import CrearSensor from "./CrearSensor";
import TableroSensores from "./TableroSensores";
import AdministrarSensores from "./AdministrarSensores";

// Lee bit del bitset (string "0/1") guardado en localStorage bajo "endpoint"


const Sensores = ({ initialTab = "tablero", onTabChange }) => {
  const { can: hasBit } = usePermisos();
  const [tab, setTab] = useState(initialTab);
  const lastAuthTickRef = useRef(0);
  const reportedFallbackRef = useRef("");

  // ===== Permisos por pestaña
  const canCrearView = hasBit("SENSORES_TAB_CREAR_VIEW") !== false;
  const canCrearEdit = hasBit("SENSORES_TAB_CREAR_EDIT") !== false;
  const canCrear = canCrearView && canCrearEdit;

  const canAdministrar = hasBit("SENSORES_TAB_EDITAR_VIEW") !== false;
  const canTablero = hasBit("SENSORES_TAB_TABLERO_VIEW") !== false;

  const tabs = useMemo(() => {
    const out = [];
    if (canTablero) out.push({ id: "tablero", label: "Tablero de sensores" });
    if (canCrear) out.push({ id: "crear", label: "Crear Sensor" });
    if (canAdministrar) out.push({ id: "administrar", label: "Administrar Sensores" });
    return out;
  }, [canCrear, canAdministrar, canTablero]);

  // Respeta la pestaña de la URL sólo cuando pertenece a Sensores. Si durante
  // una transición llega un `sub` de Inicio (p. ej. "resumen"), normalízalo
  // una sola vez para evitar el ciclo resumen <-> administrar.
  useEffect(() => {
    const firstAllowed = tabs[0]?.id ?? "";
    const initialIsValid = tabs.some((t) => t.id === initialTab);
    const currentIsValid = tabs.some((t) => t.id === tab);
    const next = initialIsValid ? initialTab : currentIsValid ? tab : firstAllowed;

    if (next !== tab) setTab(next);

    if (!initialIsValid && next && reportedFallbackRef.current !== next) {
      reportedFallbackRef.current = next;
      onTabChange?.(next);
    } else if (initialIsValid) {
      reportedFallbackRef.current = "";
    }
  }, [initialTab, tabs, tab, onTabChange]);

  const handleTabChange = (newTab) => {
    setTab(newTab);
    onTabChange?.(newTab); // el Dashboard espeja esto a ?sub=
  };

  // ===== Soft refresh: volver a cargar datos de la pestaña activa sin navegar
  // Lo usamos en FOCUS/VISIBILITY/AUTH-CHANGED para que F5 o volver de otra pestaña “actualice adentro”.
  const softRefresh = useCallback(() => {
    // Si necesitas disparar algo específico por tab, lo haces aquí.
    // Por ahora delegamos en los propios componentes (AdministrarSensores ya re-fetch-ea al montar/foco).
    // Si en el futuro CrearSensor necesita refrescar catálogos, puedes propagar onSoftRefresh a ese hijo.
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") softRefresh();
    };
    const onFocus = () => softRefresh();
    const onAuthChanged = () => {
      // De-bounce básico por si el evento se emite varias veces en cadena
      const now = Date.now();
      if (now - lastAuthTickRef.current > 200) {
        lastAuthTickRef.current = now;
        softRefresh();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("auth-changed", onAuthChanged);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("auth-changed", onAuthChanged);
    };
  }, [softRefresh]);

  return (
    <div className="bg-neutral-900 text-white p-2 sm:p-6 min-w-0 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
      <h1 className="text-center text-2xl sm:text-3xl font-bold text-green-500 mb-4 sm:mb-6">
        GESTIÓN DE SENSORES
      </h1>

      {/* Tabs (solo los permitidos) */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => handleTabChange(t.id)}
            className={`w-full min-h-12 px-4 py-2 rounded text-sm sm:text-base transition-all ${
              tab === t.id
                ? "bg-green-600 hover:bg-green-700"
                : "bg-gray-700 hover:bg-gray-600"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Contenido dinámico */}
      {tab === "tablero" && canTablero && <TableroSensores />}
      {tab === "crear" && canCrear && (
        <CrearSensor
          // onSoftRefresh={softRefresh} // úsalo si CrearSensor requiere refrescar catálogos al volver foco
        />
      )}

      {tab === "administrar" && canAdministrar && (
        <AdministrarSensores
          // onSoftRefresh={softRefresh} // opcional; AdministrarSensores ya hace re-fetch al montar/foco
        />
      )}

      {/* Sin pestañas visibles */}
      {tabs.length === 0 && (
        <div className="bg-neutral-800 border border-neutral-700 rounded p-4 text-center">
          <p className="text-sm text-neutral-300">
            No tienes permisos para ver pestañas en Sensores.
          </p>
        </div>
      )}
    </div>
  );
};

export default Sensores;
